import React from 'react';
import { LazySearchHeader } from 'owa-mail-search';
import type { SearchTableQuery } from 'owa-mail-list-search';
import { getSelectedTableView } from 'owa-mail-list-store';
import { SelectAllCheckbox } from 'owa-mail-list-view';
import { getIsNonMailEntitySearchTab, TabbedSearchPane } from 'owa-tabbed-search';
import MailView from './MailView';
import { observer } from 'owa-mobx-react';
import { SearchScopeKind } from 'owa-search-service/lib/data/schema/SearchScope';
import {
    searchResults,
    searchResultsFromArchive,
} from 'owa-search-locstrings/lib/searchResults.locstring.json';
import loc from 'owa-localize';
import {
    searchHeaderBorders,
    searchHeaderBordersBleedThrough,
    searchHeaderBordersBleedThroughNoBorder,
    searchHeaderBordersBleedThroughNoBorderNoBlur,
    searchHeaderBordersNeutral,
    searchHeaderBordersDark,
    searchHeaderBordersLight,
    searchResultsContainer,
    searchResultsContainerBorder,
    searchResultsTabbedContainer,
    searchResultsTabbedContainerDenseless,
    newSearchHeaderContainer,
    fullBleed,
    newSearchHeaderContainerMargin,
    searchHeaderLeftBordersBleedThrough,
    searchNeutralLeftMargin,
    bizChatInReadingPaneStyles,
} from './MailModule.scss';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import { isCapabilityEnabled } from 'owa-capabilities';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getIsDarkTheme } from 'owa-fabric-theme';
import classNames from 'owa-classnames';
import { SearchProvider } from 'owa-search-service/lib/data/schema/SearchProvider';
import { shouldShowFolderPane } from 'owa-mail-layout';
import { shouldShowBizChatInReadingPane } from 'owa-copilot-chat-drawer-store';

const defaultExport: React.FunctionComponent = observer(function SearchView() {
    const tableView = getSelectedTableView();
    const tableViewId = tableView.id;
    const searchTableQuery = tableView?.tableQuery as SearchTableQuery;
    const showTabbedSearchPane = getIsNonMailEntitySearchTab();
    const isPrimaryMailBox = searchTableQuery.searchScope?.kind === SearchScopeKind.PrimaryMailbox;
    const searchResultHeaderText =
        searchTableQuery.searchScope?.kind == SearchScopeKind.ArchiveMailbox
            ? loc(searchResultsFromArchive)
            : loc(searchResults);
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');
    const isDarkMode = getIsDarkTheme();
    const showFolderPane = shouldShowFolderPane();
    const showBizChatInReadingPane = shouldShowBizChatInReadingPane(window);

    const getSearchHeader = React.useMemo(() => {
        if (searchTableQuery == undefined || isFeatureEnabled('sea-searchRibbon')) {
            return;
        } else {
            return (
                <div
                    className={classNames(
                        newSearchHeaderContainerMargin,
                        isBleedThroughEnabled && !isRPDenselessEnabled && fullBleed,
                        isRPDenselessEnabled && !isBleedThroughEnabled && searchNeutralLeftMargin
                    )}
                >
                    <div
                        className={classNames(
                            isRPDenselessEnabled
                                ? isBleedThroughEnabled
                                    ? showFolderPane
                                        ? searchHeaderBordersBleedThrough
                                        : searchHeaderBordersBleedThroughNoBorder
                                    : searchHeaderBordersNeutral
                                : searchHeaderBorders,
                            isRPDenselessEnabled &&
                                !showFolderPane &&
                                searchHeaderLeftBordersBleedThrough,
                            isRPDenselessEnabled &&
                                (isDarkMode ? searchHeaderBordersDark : searchHeaderBordersLight),
                            showBizChatInReadingPane && bizChatInReadingPaneStyles
                        )}
                    >
                        <LazySearchHeader
                            searchScenarioId={searchTableQuery.searchScenarioId}
                            noTabsEnabledTitleText={searchResultHeaderText}
                            isPrimaryMailBox={isPrimaryMailBox}
                            isBleedThroughEnabled={isBleedThroughEnabled}
                            isLocalSearch={
                                searchTableQuery.searchProvider === SearchProvider.Offline ||
                                searchTableQuery.searchProvider === SearchProvider.PST ||
                                searchTableQuery.searchProvider === SearchProvider.UnifiedInbox
                            }
                        >
                            <SelectAllCheckbox
                                tableViewId={tableViewId}
                                isInboxWithPivots={true}
                                isSearchRender={true}
                            />
                        </LazySearchHeader>
                    </div>
                </div>
            );
        }
    }, [
        tableViewId,
        searchTableQuery,
        isBleedThroughEnabled,
        isRPDenselessEnabled,
        isDarkMode,
        showFolderPane,
        showBizChatInReadingPane,
    ]);

    const getSearchResultsContainer = React.useMemo(() => {
        if (showTabbedSearchPane) {
            return (
                <div
                    className={classNames(
                        searchResultsContainer,
                        isRPDenselessEnabled
                            ? searchResultsTabbedContainerDenseless
                            : searchResultsTabbedContainer
                    )}
                >
                    <TabbedSearchPane header={undefined} />
                </div>
            );
        } else {
            return (
                <div
                    className={classNames(
                        searchResultsContainer,
                        isFeatureEnabled('sea-searchRibbon') && searchResultsContainerBorder,
                        isFeatureEnabled('sea-searchRibbon') &&
                            isFeatureEnabled('mon-copilot-bizchat-drawer-readingPane') &&
                            showBizChatInReadingPane &&
                            searchHeaderBordersBleedThroughNoBorderNoBlur
                    )}
                >
                    <MailView />
                </div>
            );
        }
    }, [showTabbedSearchPane, isRPDenselessEnabled, showBizChatInReadingPane]);

    return (
        <div className={newSearchHeaderContainer}>
            {getSearchHeader}
            {getSearchResultsContainer}
        </div>
    );
}, 'SearchView');
export default defaultExport;
